import { connect } from "react-redux";
import { classNames } from "primereact/utils";
import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { ScheduleViewModel } from "../../viewmodels/schedule/ScheduleViewModel";

import { format, addMonths, subMonths } from "date-fns";
import { useEffect, useState } from "react";
import EmptyModal from "../../components/modals/EmptyModal";

function Schedule(props) {
  const { addSnackbar, logout } = props;

  const {
    emptyModal,
    calendar,
    currentMonth,
    onChangeMonth,
    getCalendar,
    openWoDetails,
    setEmptyModelOpen,
    selectedDay,
    setSelectedDay,
    handleDaySelect,
  } = ScheduleViewModel(addSnackbar, logout);

  // Tracks which day cell is expanded inline (by ISO date string, e.g. "2025-08-14")
  const [expandedDay, setExpandedDay] = useState(null);

  useEffect(() => {
    getCalendar();
  }, []);

  const onDayClick = (day) => {
    handleDaySelect(day.date);
    setSelectedDay({ ...day, isSelected: true });
  };

  const onMoreClick = (e, day) => {
    e.stopPropagation();
    // Expand inline AND show all events in the bottom panel
    setExpandedDay(day.date);
    onDayClick(day);
  };

  const onShowLessClick = (e, day) => {
    e.stopPropagation();
    setExpandedDay((prev) => (prev === day.date ? null : prev));
    // Keep the bottom panel as-is (still selected day)
  };

  return (
    <div className="px-4 lg:flex lg:h-full lg:flex-col">
      <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 lg:flex-none">
        <h1 className="text-base font-semibold text-gray-900"></h1>
        <div className="flex items-center">
          <div className="relative flex items-center rounded-md bg-white shadow-xs md:items-stretch">
            <button
              type="button"
              className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
              onClick={() => onChangeMonth(subMonths(currentMonth, 1))}
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="size-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
              onClick={() => onChangeMonth(new Date())}
            >
              {format(currentMonth, "MMMM yyyy")}
            </button>
            <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
            <button
              type="button"
              className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
              onClick={() => onChangeMonth(addMonths(currentMonth, 1))}
            >
              <span className="sr-only">Next month</span>
              <ChevronRightIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            className="ml-4 hidden border rounded border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block h-9"
            onClick={() => onChangeMonth(new Date())}
          >
            Today
          </button>
          <div className="hidden md:ml-4 md:flex md:items-center">
            <button
              type="button"
              className="ml-6 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => {
                window.location = "/new-work-order";
              }}
            >
              Create WO
            </button>
          </div>
        </div>
      </header>

      <div className="shadow-sm ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col">
        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs/6 font-semibold text-gray-700 lg:flex-none">
          <div className="bg-white py-2">
            M<span className="sr-only sm:not-sr-only">on</span>
          </div>
          <div className="bg-white py-2">
            T<span className="sr-only sm:not-sr-only">ue</span>
          </div>
          <div className="bg-white py-2">
            W<span className="sr-only sm:not-sr-only">ed</span>
          </div>
          <div className="bg-white py-2">
            T<span className="sr-only sm:not-sr-only">hu</span>
          </div>
          <div className="bg-white py-2">
            F<span className="sr-only sm:not-sr-only">ri</span>
          </div>
          <div className="bg-white py-2">
            S<span className="sr-only sm:not-sr-only">at</span>
          </div>
          <div className="bg-white py-2">
            S<span className="sr-only sm:not-sr-only">un</span>
          </div>
        </div>

        <div className="flex bg-gray-200 text-xs/6 text-gray-700 lg:flex-auto">
          {/* Desktop grid */}
          <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
            {calendar.map((day) => (
              <div
                key={day.date}
                className={classNames(
                  day.isCurrentMonth ? "bg-white" : "bg-gray-50 text-gray-500",
                  "relative px-1 py-2"
                )}
                onClick={() => onDayClick(day)}
              >
                <time
                  dateTime={day.date}
                  className={
                    day.isToday
                      ? "flex size-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white"
                      : day.isSelected && !day.isToday
                      ? "flex size-6 items-center justify-center rounded-full bg-gray-600 font-semibold text-white"
                      : undefined
                  }
                >
                  {day.date.split("-").pop().replace(/^0/, "")}
                </time>

                {day.events.length > 0 && (
                  <ol className="mt-2 space-y-2">
                    {(expandedDay === day.date
                      ? day.events
                      : day.events.slice(0, 2)
                    ).map((event) => (
                      <li
                        key={event.id}
                        className={classNames(
                          event.status.value === 5
                            ? "bg-yellow-200 border-yellow-600"
                            : "bg-yellow-200 border-yellow-600",
                          "rounded-md border"
                        )}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openWoDetails(event);
                          }}
                          className="group text-start w-full"
                        >
                          <p className="truncate font-bold text-gray-900 group-hover:text-indigo-600 w-full max-w-full">
                            {event.title}
                          </p>
                        </button>
                      </li>
                    ))}

                    {day.events.length > 2 && expandedDay !== day.date && (
                      <li className="text-gray-500">
                        <button
                          type="button"
                          onClick={(e) => onMoreClick(e, day)}
                          className="underline hover:text-gray-700"
                          aria-label={`Show all ${day.events.length} events for ${day.date}`}
                        >
                          + {day.events.length - 2} more
                        </button>
                      </li>
                    )}

                    {day.events.length > 2 && expandedDay === day.date && (
                      <li className="text-gray-500">
                        <button
                          type="button"
                          onClick={(e) => onShowLessClick(e, day)}
                          className="underline hover:text-gray-700"
                        >
                          Show less
                        </button>
                      </li>
                    )}
                  </ol>
                )}
              </div>
            ))}
          </div>

          {/* Mobile grid */}
          <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
            {calendar.map((day) => (
              <button
                key={day.date}
                type="button"
                className={classNames(
                  day.isCurrentMonth ? "bg-white" : "bg-gray-50",
                  (day.isSelected || day.isToday) && "font-semibold",
                  day.isSelected && "text-white",
                  !day.isSelected && day.isToday && "text-indigo-600",
                  !day.isSelected &&
                    day.isCurrentMonth &&
                    !day.isToday &&
                    "text-gray-900",
                  !day.isSelected &&
                    !day.isCurrentMonth &&
                    !day.isToday &&
                    "text-gray-500",
                  "flex h-14 flex-col px-3 py-2 hover:bg-gray-100 focus:z-10"
                )}
                onClick={() => onDayClick(day)}
              >
                <time
                  dateTime={day.date}
                  className={classNames(
                    day.isSelected &&
                      "flex size-6 items-center justify-center rounded-full",
                    day.isSelected && day.isToday && "bg-indigo-600",
                    day.isSelected && !day.isToday && "bg-gray-500",
                    "ml-auto"
                  )}
                >
                  {day.date.split("-").pop().replace(/^0/, "")}
                </time>
                <span className="sr-only">{day.events.length} events</span>
                {day.events.length > 0 && (
                  <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                    {day.events.map((event) => (
                      <span
                        key={event.id}
                        className="mx-0.5 mb-1 size-1.5 rounded-full bg-gray-400"
                      />
                    ))}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom panel shows all events for the selected day */}
      {selectedDay?.events.length > 0 && (
        <div className="px-4 py-10 sm:px-6">
          <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow-sm ring-1 ring-black/5">
            {selectedDay.events.map((event) => (
              <li
                key={event.id}
                className="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50"
              >
                <div className="flex-auto">
                  <p className="font-semibold text-gray-900">{event.title}</p>
                </div>
                <button
                  onClick={() => openWoDetails(event)}
                  className="ml-6 flex-none self-center rounded-md bg-white px-3 py-2 font-semibold text-gray-900 opacity-0 shadow-xs ring-1 ring-gray-300 ring-inset group-hover:opacity-100 hover:ring-gray-400 focus:opacity-100"
                >
                  View<span className="sr-only">, {event.title}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Schedule)
);
